import { shouldShowJoinLeaderboardTile, UserProfile } from 'fitbeat.gym-core';
import { IUserLeaderboardViewModel, ProgramType, UserTargetTypes } from 'fitbeat.models';
import moment from 'moment';
import React, { FunctionComponent } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { moderateScale } from 'react-native-size-matters';
import { connect } from 'react-redux';
import Theme from '../../theme';
import { SetupActionButton } from '../../ui/components';
import { getSkillLevelText, isWeb } from '../../utils';
import { defaultScale, scaleText } from '../../utils/scaling';
import {
  currentWeightInfoFromGoalSelector,
  showMileStoneSelector,
  weightMilestoneInfoFromGoalSelector,
  weightTargetInfoFromGoalSelector,
} from '../../WeightTarget/selectors';
import { mappedTitles } from '../../WorkoutGoalAssistant/utils';
import { openLeaderboardModal } from '../actions';
import { getMinsScheduledSelector } from '../selectors';
import { ChallengeInfoDisplay } from './ChallengeInfoDisplay';
import { LeaderboardOverview } from './LeaderboardOverview';
import { IWeeklyGoalData } from '../types';

interface IProps {
  userProfile: UserProfile;
  weeklyGoalData: IWeeklyGoalData | null;
  isFetchingWeeklyGoalData: boolean;
  toggleWeightTargetModal: (visible: boolean) => void;
  weeklyGoalDataError: boolean;
  isFocused: boolean;
  weightTarget: any;
  weightMilestone: any;
  showLogYourWeightButton: boolean;
  showMileStone: boolean;
  scheduledMins: number;
  navigateToBodyMetricsTab: () => void;
  navigateToWorkoutsTab: () => void;
  navigateToChallengesTab: () => void;
  t: (str: string, object?: { [key: string]: string }) => string;
  currentWeight: number;
  openPrimaryLeaderboard: (leaderboard: IUserLeaderboardViewModel) => void;
}

const WeeklyGoal: FunctionComponent<IProps> = ({
  weeklyGoalData,
  userProfile,
  t,
  toggleWeightTargetModal,
  weightTarget,
  weightMilestone,
  showMileStone,
  navigateToBodyMetricsTab,
  navigateToWorkoutsTab,
  navigateToChallengesTab,
  currentWeight,
  ...props
}) => {
  const goalTitle = mappedTitles[userProfile.longTermGoal];
  const skillLevel = getSkillLevelText[userProfile.skillLevel];
  const goalIsMuscleAndStrength =
    userProfile.longTermGoal === ProgramType.GainMuscleAndStrengthGoal;
  const radius = moderateScale(90);
  const containerHeight = radius * 2;
  const weightUnit = weightTarget ? weightTarget.unit :
   userProfile.useImperialUnit ? t('formFields:lb') : t('formField:kg');
  const hasScale = UserProfile.hasSmartScale(userProfile);

  const getTarget = () => {
    let target;

    if (!weightTarget || !weightTarget.targetType) {
      return 0;
    }

    switch (weightTarget.targetType) {
      case UserTargetTypes.Musclemass:
        target = weightTarget.muscleMass;
        break;
      case UserTargetTypes.Bodyweight:
        target = weightTarget.weight;
        break;
      case UserTargetTypes.Bodyfat:
        target = weightTarget.bodyFat;
        break;
    }

    return Math.round(target * 10) / 10;
  }

  const getMilestone = () => {
    let milestone;

    if (!weightTarget || !weightTarget.targetType) {
      return 0;
    }

    switch (weightTarget.targetType) {
      case UserTargetTypes.Musclemass:
        milestone = weightMilestone.muscleMass;
        break;
      case UserTargetTypes.Bodyweight:
        milestone = weightMilestone.weight;
        break;
      case UserTargetTypes.Bodyfat:
        milestone = weightMilestone.bodyFat;
        break;
    }

    return Math.round(milestone * 10) / 10;
}

  const RenderStartTrackingOrSummary = () => {
    const cannotStartTrackingProgress = goalIsMuscleAndStrength && !hasScale;
    if (cannotStartTrackingProgress) {
      return (
        <View style={styles.container}>
          <View style={styles.goalTextContainer}>
            <Text style={styles.goalText}>{goalTitle}</Text>
            <Text style={styles.descriptionText}>{t('fitnessGoal')}</Text>
          </View>
      </View>
      );
    } else {
      return (
        <View style={styles.container}>
          <SetupActionButton
            onPress={toggleWeightTargetModal}
            text={t('startTrackingProgress')}
            containerStyle={styles.textPlusButton}
            textStyle={{...Theme.textStyle.small}}
            iconSize={defaultScale(20)}
          />
        </View>
      );
    }
  };

  return (
    <View>
      <View style={styles.loadingIndicator}>
        {props.isFetchingWeeklyGoalData && (
          <ActivityIndicator
            animating
            color={Theme.PRIMARY}
            style={[
              styles.activityIndicator,
              {
                height: containerHeight,
                width: containerHeight,
              },
            ]}
          />
        )}
      </View>
      {!props.isFetchingWeeklyGoalData && weeklyGoalData && (
      <View>
        {weeklyGoalData.primaryLeaderboard && <View >
          <TouchableOpacity
            style={[{flex: 1}, styles.border]}
            onPress={() => props.openPrimaryLeaderboard(weeklyGoalData.primaryLeaderboard)}
          >
            <LeaderboardOverview
              userLeaderboardData={weeklyGoalData.primaryLeaderboard}
              t={t}
              showIcons={!isWeb()}
              allowPress
              displayedOnDashboard
              isFeatured={true}
              showJoinLeaderboardTile={shouldShowJoinLeaderboardTile(weeklyGoalData.primaryLeaderboard)}
            />
          </TouchableOpacity>
        </View>}
        <View style={[styles.challengesContainer, !weeklyGoalData.primaryLeaderboard ? {marginTop: 0}: {}]}>
            <TouchableOpacity style={[{flex: 1}, styles.border]} onPress={navigateToWorkoutsTab}>
              <ChallengeInfoDisplay
                unit={'mins'}
                t={t}
                target={weeklyGoalData.totalTargetMinutes ?? '-'}
                currentValue={weeklyGoalData.completedMinutes ?? '-'}
                subtext={t('workoutProgressWeek')}
              />
            </TouchableOpacity>
          <View style={{paddingHorizontal: defaultScale(7)}}></View>
        {(
          <TouchableOpacity
            style={[{ flex: 1, backgroundColor: Theme.WHITE }, styles.border]}
            onPress={navigateToBodyMetricsTab}
          >
            {weightTarget && !weightTarget.reached ? (
              <>
                <View>
                  {weightMilestone && (
                    <ChallengeInfoDisplay
                      unit={weightUnit}
                      t={t}
                      target={getMilestone()}
                      currentValue={currentWeight}
                      subtext={t('reachByDate', {
                        date: moment(weightMilestone.date).format('D MMM'),
                      })}
                    />
                  )}
                </View>
              </>
            ) : <RenderStartTrackingOrSummary /> }
          </TouchableOpacity>
        )}
        </View>
      </View>)}
    </View>
  );
};

const styles = StyleSheet.create({
  loadingIndicator: {
    flex: 1,
    alignSelf: 'center',
  },
  border: {
    borderRadius: defaultScale(3),
    overflow: 'hidden',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: Theme.GREY,
  },
  challengesContainer: {
    flexDirection: 'row',
    flex: 1,
    marginTop: defaultScale(11),
    marginBottom: defaultScale(7),
  },
  text: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_MEDIUM,
    textAlign: 'center',
  },
  textPlusButton: {
    flex: 1,
    justifyContent: 'center',
    ...Theme.textStyle.small,
  },
  textBold: {
    fontFamily: Theme.Custom800,
    color: Theme.GREY_DARK,
  },
  activityIndicator: {
    marginBottom: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  verticalDivider: {
    backgroundColor: Theme.GREY_LIGHT,
    width: 1,
    height: '95%',
  },
  horizontalDivider: {
    width: '80%',
    height: 1,
    backgroundColor: Theme.GREY_LIGHT,
    marginLeft: defaultScale(20),
  },
  container: {
    justifyContent: 'center',
    flex: 1,
  },
  goalTextContainer: {
    justifyContent: 'center',
    backgroundColor: Theme.WHITE,
  },
  goalText: {
    ...Theme.textStyle.bodyCopyBold,
    paddingLeft: defaultScale(20),
  },
  descriptionText: {
    ...Theme.textStyle.small,
    color: Theme.GREY_DARK,
    paddingLeft: defaultScale(20),
  },
});

const mapStateToProps = (state: any) => {
  return {
    weeklyGoalData: !state.summaryDashboard.view.weeklyGoalDataError
      ? state.summaryDashboard.data.weeklyGoalData
      : {
          completedMinutes: '-',
          scheduledMinutes: 0,
          totalTargetMinutes: '-',
        },
    isFetchingWeeklyGoalData:
      state.summaryDashboard.view.isFetchingWeeklyGoalData,
    weeklyGoalDataError: state.summaryDashboard.view.weeklyGoalDataError,
    showMileStone: showMileStoneSelector(state),
    scheduledMins: getMinsScheduledSelector(state),
    weightMilestone: weightMilestoneInfoFromGoalSelector(state),
    weightTarget: weightTargetInfoFromGoalSelector(state),
    currentWeight: currentWeightInfoFromGoalSelector(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    openPrimaryLeaderboard: (leaderboard: IUserLeaderboardViewModel) =>
      dispatch(openLeaderboardModal(leaderboard, true, false)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WeeklyGoal);
